import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCopy, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { customStyles } from "../../tableStyles";
import { DataTableLoader, GoldLoader } from "../shared/Loaders";
import "../css/PersonList.css";
import "../css/EmailCopyControl.css";
import "../css/CrmSearchBar.css";

/** Local month (0–11) and day of month from stored DOB; null if missing/invalid. */
function parseMonthDayFromDob(dob) {
  if (dob == null || dob === "") return null;
  const dt = dob instanceof Date ? dob : new Date(dob);
  if (Number.isNaN(dt.getTime())) return null;
  return { month: dt.getMonth(), day: dt.getDate() };
}

function compareMonthDay(a, b) {
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}

/** Readable month + day, no year (e.g. "Apr 10"). Uses local MD from DOB. */
function formatBirthdayMonthDay(dob) {
  const md = parseMonthDayFromDob(dob);
  if (!md) return "—";
  const anchor = new Date(2000, md.month, md.day);
  if (Number.isNaN(anchor.getTime())) return "—";
  return anchor.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

/** Sort key for table (month-major then day). */
function birthdaySortKey(dob) {
  const md = parseMonthDayFromDob(dob);
  if (!md) return 0;
  return md.month * 100 + md.day;
}

/**
 * Include individuals whose birthday (month/day) falls on or after today through Dec 31
 * (same calendar year, local timezone). Excludes earlier MD (already passed this year).
 * Sorted by nearest upcoming month/day first (e.g. Apr 10 before Apr 15 before May 1).
 */
function filterUpcomingBirthdaysSorted(persons) {
  const now = new Date();
  const todayMD = { month: now.getMonth(), day: now.getDate() };

  return persons
    .map((p) => ({
      person: p,
      md: parseMonthDayFromDob(p.date_of_birth),
    }))
    .filter(({ md }) => md != null)
    .filter(({ md }) => compareMonthDay(md, todayMD) >= 0)
    .sort((a, b) => compareMonthDay(a.md, b.md))
    .map(({ person }) => person);
}

const UpcomingBirthdays = () => {
  const [persons, setPersons] = useState([]);
  const [filteredPersons, setFilteredPersons] = useState([]);
  const [organisations, setOrganisations] = useState({});
  const [roles, setRoles] = useState({});
  const [personRoles, setPersonRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [personRes, orgResponse, roleResponse, personRoleResponse] =
          await Promise.all([
            axios.get(`${process.env.REACT_APP_API_URL}/persons`),
            axios.get(`${process.env.REACT_APP_API_URL}/organisations`),
            axios.get(`${process.env.REACT_APP_API_URL}/roles`),
            axios.get(`${process.env.REACT_APP_API_URL}/personRoles`),
          ]);

        const all = Array.isArray(personRes.data) ? personRes.data : [];
        const list = filterUpcomingBirthdaysSorted(all);

        const orgMap = orgResponse.data.reduce((map, org) => {
          map[org._id] = org.name;
          return map;
        }, {});

        const roleMap = roleResponse.data.reduce((map, role) => {
          map[role._id] = role.role_name;
          return map;
        }, {});

        setPersons(list);
        setFilteredPersons(list);
        setOrganisations(orgMap);
        setRoles(roleMap);
        setPersonRoles(personRoleResponse.data);
      } catch (error) {
        console.error("Error fetching upcoming birthdays:", error);
        setPersons([]);
        setFilteredPersons([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setToastMessage("Email copied to clipboard!");
    setShowToast(false);
    setTimeout(() => {
      setShowToast(true);
    }, 10);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  const getRelationships = useCallback(
    (personId) => {
      const relations = personRoles.filter(
        (role) => String(role.person_id) === String(personId)
      );
      return relations.map((relation) => ({
        organisation: organisations[relation.org_id] || "Unknown",
        role: roles[relation.role_id] || "Unknown",
      }));
    },
    [personRoles, organisations, roles]
  );

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    setCurrentPage(1);

    const filtered = persons.filter((person) => {
      const relationships = getRelationships(person._id);
      const orgNames = relationships
        .map((r) => r.organisation.toLowerCase())
        .join(" ");
      const roleNames = relationships
        .map((r) => r.role.toLowerCase())
        .join(" ");
      const fullName =
        `${person.name} ${person.mname || ""} ${person.lname || ""}`.toLowerCase();
      const birthdayLabel = formatBirthdayMonthDay(person.date_of_birth).toLowerCase();
      return (
        fullName.includes(value) ||
        (person.email && person.email.toLowerCase().includes(value)) ||
        orgNames.includes(value) ||
        roleNames.includes(value) ||
        birthdayLabel.includes(value)
      );
    });
    setFilteredPersons(filtered);
  };

  const handleRowClick = (row) => {
    navigate(`/edit-person/${row._id}`, { state: { person: row } });
  };

  const columns = [
    {
      name: "#",
      width: "56px",
      center: true,
      sortable: false,
      cell: (row, rowIndex) =>
        (currentPage - 1) * rowsPerPage + rowIndex + 1,
    },
    {
      name: "Birthday",
      width: "104px",
      left: true,
      sortable: true,
      selector: (row) => birthdaySortKey(row.date_of_birth),
      cell: (row) => (
        <span style={{ color: "#000" }}>
          {formatBirthdayMonthDay(row.date_of_birth)}
        </span>
      ),
    },
    {
      name: "Image",
      selector: (row) => row.image,
      width: "80px",
      left: true,
      cell: (row) => (
        <img
          src={row.image ? `/images/${row.image}` : "/images/avatar.png"}
          alt={row.name}
          style={{
            width: "50px",
            height: "50px",
            borderRadius: "4px",
            padding: "3px",
          }}
        />
      ),
      ignoreRowClick: true,
    },
    {
      name: "Name",
      selector: (row) => `${row.name} ${row.mname || ""} ${row.lname}`,
      sortable: true,
      left: true,
      cell: (row) => (
        <span
          style={{ color: "#000", cursor: "pointer" }}
          onClick={() => handleRowClick(row)}
        >
          {row.name} {row.mname || ""} {row.lname}
        </span>
      ),
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
      width: "450px",
      style: { paddingLeft: "10px" },
      left: true,
      cell: (row) => (
        <div className="email-copy-cell">
          <span className="email-text">{row.email}</span>
          <button
            type="button"
            className="email-copy-btn"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(row.email);
            }}
            title="Copy Email"
          >
            <FontAwesomeIcon icon={faCopy} style={{ color: "#D9A128" }} />
          </button>
        </div>
      ),
    },
    {
      name: "Organisations",
      left: true,
      grow: 2,
      cell: (row) => {
        const relationships = getRelationships(row._id);
        return (
          <ul>
            {relationships.map((relation, index) => (
              <li key={index} style={{ padding: "4px" }}>
                {relation.organisation}
              </li>
            ))}
          </ul>
        );
      },
    },
    {
      name: "Roles",
      left: true,
      grow: 1,
      cell: (row) => {
        const relationships = getRelationships(row._id);
        return (
          <ul>
            {relationships.map((relation, index) => (
              <li key={index}>{relation.role}</li>
            ))}
          </ul>
        );
      },
    },
  ];

  if (loading && persons.length === 0) {
    return <GoldLoader label="Loading..." variant="page" />;
  }

  return (
    <div className="crm-list-page">
      <div className="crm-list-toolbar d-flex justify-content-between mb-1">
        <div className="me-3 mb-1 crm-search-wide">
          <div className="crm-search-wrapper individuals-search-wrapper">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="crm-search-icon" />
            <input
              type="text"
              placeholder="Search by name, email, organisation, role, or birthday"
              className="crm-search-input form-control search-input"
              value={searchTerm}
              onChange={handleSearch}
              title="Search individuals"
            />
          </div>
        </div>

        <div className="show-section">
          <span>Show</span>
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="show-dropdown-select"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      <div className="crm-table-wrapper">
        <DataTable
          key={rowsPerPage}
          columns={columns}
          data={filteredPersons}
          progressPending={loading}
          progressComponent={<DataTableLoader />}
          pagination
          paginationPerPage={rowsPerPage}
          paginationComponentOptions={{ noRowsPerPage: true }}
          onChangePage={setCurrentPage}
          highlightOnHover
          striped
          responsive
          customStyles={customStyles}
          onRowClicked={handleRowClick}
        />
      </div>

      {showToast && (
        <div className="copy-toast">
          <FontAwesomeIcon
            icon={faCheck}
            style={{ marginRight: "10px", color: "#D9A128" }}
          />
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default UpcomingBirthdays;
